import React, { useCallback } from "react";
import { connect } from "react-redux";

import { Tooltip } from "antd";
import { Col } from "antd/es/grid";
import Row from "antd/es/row";
import { useIntl } from "react-intl";

import EmailSyncProgress from "@mapmycustomers/shared/types/email/EmailSyncProgress";
import { Button } from "@mapmycustomers/ui";

import NylasType from "@app/enum/NylasType";
import { connectToMailNylas } from "@app/scene/settings/util/connectToNylas";
import { getEmailSyncProgress } from "@app/store/email";
import { getEmailNylasInfo } from "@app/store/nylas";
import { deleteNylas } from "@app/store/nylas/actions";
import { RootState } from "@app/store/rootReducer";
import layout from "@app/styles/layout";
import NylasInfo from "@app/types/nylas/NylasInfo";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";

import styles from "../EmailPreferences.module.scss";

import messages from "./messages";

interface Props {
  emailNylasInfo: NylasInfo | undefined;
  emailSyncProgress?: EmailSyncProgress;
  onDeleteNylas: typeof deleteNylas.request;
  onDisconnect: () => void;
}

const NylasConnect: React.FC<Props> = ({
  emailNylasInfo,
  emailSyncProgress,
  onDeleteNylas,
  onDisconnect,
}) => {
  const intl = useIntl();

  const analytics = useExtendedAnalytics(["Connected Email"]);

  const handleDisconnect = useCallback(() => {
    if (emailNylasInfo) {
      onDeleteNylas({ nylasId: emailNylasInfo.id, type: NylasType.EMAIL });
      analytics.clicked(["Disconnect"]);
      onDisconnect();
    }
  }, [analytics, emailNylasInfo, onDeleteNylas, onDisconnect]);

  const handleChangeEmail = useCallback(() => {
    connectToMailNylas();
    analytics.clicked(["Change Email"]);
  }, [analytics]);

  if (!emailNylasInfo) {
    return null;
  }

  const disabled = !!emailSyncProgress?.syncing;
  const disabledTooltipTitle = disabled
    ? intl.formatMessage(messages.disabledWhileSyncInProgress)
    : null;

  return (
    <Row justify="space-between">
      <Col>
        <div className={styles.settingLabel}>
          {intl.formatMessage(messages.connectedEmailTitle)}
        </div>
        <div className={styles.settingSubLabel}>
          {emailNylasInfo?.connectedEmail ?? emailNylasInfo.user.username}
        </div>
      </Col>
      <Col>
        <Row gutter={layout.spacerS}>
          <Col>
            <Tooltip title={disabledTooltipTitle}>
              <Button disabled={disabled} onClick={handleDisconnect} size="small">
                {intl.formatMessage(messages.connectedEmailDisconnect)}
              </Button>
            </Tooltip>
          </Col>
          <Col>
            <Tooltip title={disabledTooltipTitle}>
              <Button disabled={disabled} onClick={handleChangeEmail} size="small" type="filled">
                {intl.formatMessage(messages.connectedEmailDisconnectChangeEmail)}
              </Button>
            </Tooltip>
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

const mapStateToProps = (state: RootState) => ({
  emailNylasInfo: getEmailNylasInfo(state),
  emailSyncProgress: getEmailSyncProgress(state),
});

const mapDispatchToProps = {
  onDeleteNylas: deleteNylas.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(NylasConnect);
